// src/cockpit4/components/MarketingCalendar.tsx
// ============================================
// P0-功能1：智能营销日历（营销中心）
//
// 功能：
//   1. 月/周视图切换的可视化营销日历
//   2. 事件卡片：平台图标 + 标题 + 状态（待生成/已生成/待发布/已发布）
//   3. AI 基于营销节点库（节日/节气/电商大促/行业热点）生成月度计划
//   4. 一键跳转到媒体工作室生成对应内容
//   5. 持久化到 Vault：10_Operations（运营层）/Marketing/Calendar/{年}-{月}.md
//
// 数据来源：
//   - 营销节点库（内置：节日/节气/电商大促）
//   - useDashboardData 的 marketing 数据（各平台已有内容）
//   - Vault 中已生成的媒体包（MediaStudio.list_packages）
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Loader2,
  RefreshCw,
  Clock,
  CheckCircle2,
  FileText,
  Send,
  AlertCircle,
  ExternalLink,
  Plus,
} from "lucide-react";
import { useCockpit4Store } from "../store";
import { useDashboardData } from "../../hooks/useDashboardData";
import type { MarketingPlatform } from "../../components/Dashboard/MarketingDashboard";

// ============================================
// 类型定义
// ============================================

type EventStatus = "planned" | "generating" | "ready" | "published" | "failed";

interface CalendarEvent {
  id: string;
  date: string; // YYYY-MM-DD
  platform: MarketingPlatform;
  title: string;
  status: EventStatus;
  /** 关联的 MediaPackage ID（如有） */
  packageId?: string;
  /** 营销节点类型（节日/节气/大促/行业/自定义） */
  nodeType?: "holiday" | "solar_term" | "promotion" | "industry" | "custom";
  /** 备注 */
  note?: string;
}

// ============================================
// 营销节点库（内置）
// ============================================

interface MarketingNode {
  date: string; // MM-DD 格式
  name: string;
  type: "holiday" | "solar_term" | "promotion" | "industry";
  platforms: MarketingPlatform[]; // 适合发布的平台
  /** 内容方向建议 */
  contentAngle: string;
}

const MARKETING_NODES: MarketingNode[] = [
  // 1月
  { date: "01-01", name: "元旦", type: "holiday", platforms: ["wechat_official", "xiaohongshu"], contentAngle: "新年寄语、年度总结、新年计划" },
  // 2月
  { date: "02-14", name: "情人节", type: "holiday", platforms: ["xiaohongshu", "douyin_personal"], contentAngle: "浪漫主题、情侣场景、情感营销" },
  // 3月
  { date: "03-08", name: "妇女节", type: "holiday", platforms: ["wechat_official", "xiaohongshu"], contentAngle: "女性力量、她经济、品牌致敬" },
  { date: "03-15", name: "消费者权益日", type: "industry", platforms: ["wechat_official"], contentAngle: "品质承诺、用户信任、服务保障" },
  // 4月
  { date: "04-05", name: "清明节", type: "holiday", platforms: ["wechat_official"], contentAngle: "踏青、思念、传统文化" },
  // 5月
  { date: "05-01", name: "劳动节", type: "holiday", platforms: ["wechat_official", "douyin_enterprise"], contentAngle: "致敬劳动者、品牌故事、奋斗精神" },
  { date: "05-20", name: "520表白日", type: "promotion", platforms: ["xiaohongshu", "douyin_personal"], contentAngle: "爱的表白、限定产品、互动营销" },
  // 6月
  { date: "06-01", name: "儿童节", type: "holiday", platforms: ["xiaohongshu", "douyin_personal"], contentAngle: "童心、亲子、怀旧营销" },
  { date: "06-18", name: "618大促", type: "promotion", platforms: ["wechat_official", "xiaohongshu", "douyin_enterprise", "bilibili"], contentAngle: "年中大促、限时优惠、产品推荐" },
  // 7月
  { date: "07-07", name: "小暑", type: "solar_term", platforms: ["wechat_official"], contentAngle: "夏日养生、季节营销" },
  // 8月
  { date: "08-08", name: "立秋", type: "solar_term", platforms: ["wechat_official"], contentAngle: "秋季主题、换季推荐" },
  // 9月
  { date: "09-10", name: "教师节", type: "holiday", platforms: ["wechat_official", "xiaohongshu"], contentAngle: "感恩教师、知识传承、教育主题" },
  { date: "09-23", name: "秋分", type: "solar_term", platforms: ["wechat_official"], contentAngle: "秋季养生、平衡生活" },
  // 10月
  { date: "10-01", name: "国庆节", type: "holiday", platforms: ["wechat_official", "xiaohongshu", "douyin_enterprise"], contentAngle: "国庆主题、爱国情怀、黄金周营销" },
  // 11月
  { date: "11-11", name: "双11大促", type: "promotion", platforms: ["wechat_official", "xiaohongshu", "douyin_enterprise", "douyin_personal", "bilibili", "zhihu"], contentAngle: "全年最大促、限时折扣、产品矩阵" },
  // 12月
  { date: "12-12", name: "双12大促", type: "promotion", platforms: ["wechat_official", "xiaohongshu", "douyin_enterprise"], contentAngle: "年终冲刺、补货机会、年底清仓" },
  { date: "12-25", name: "圣诞节", type: "holiday", platforms: ["xiaohongshu", "douyin_personal"], contentAngle: "圣诞主题、节日氛围、礼物推荐" },
  { date: "12-31", name: "跨年", type: "holiday", platforms: ["wechat_official", "xiaohongshu", "douyin_personal"], contentAngle: "跨年寄语、年度回顾、新年展望" },
];

// ============================================
// 平台元信息
// ============================================

const PLATFORM_META: Record<MarketingPlatform, { label: string; color: string }> = {
  wechat_official: { label: "公众号", color: "#10B981" },
  xiaohongshu: { label: "小红书", color: "#EF4444" },
  douyin_enterprise: { label: "抖音企业号", color: "#000000" },
  douyin_personal: { label: "抖音个人号", color: "#1A1A1A" },
  zhihu: { label: "知乎", color: "#3B82F6" },
  bilibili: { label: "B站", color: "#FB7299" },
};

const STATUS_META: Record<EventStatus, { label: string; color: string; icon: typeof Clock }> = {
  planned: { label: "待生成", color: "#6B7280", icon: Clock },
  generating: { label: "生成中", color: "#F59E0B", icon: Loader2 },
  ready: { label: "待发布", color: "#3B82F6", icon: FileText },
  published: { label: "已发布", color: "#10B981", icon: CheckCircle2 },
  failed: { label: "失败", color: "#EF4444", icon: AlertCircle },
};

// ============================================
// 工具函数
// ============================================

/** 获取某月所有营销节点 */
function getNodesForMonth(year: number, month: number): MarketingNode[] {
  return MARKETING_NODES.filter((n) => {
    const [m] = n.date.split("-").map(Number);
    return m === month;
  });
}

/** 生成日历网格数据（含上下月填充） */
function generateCalendarGrid(year: number, month: number): { date: string; day: number; isCurrentMonth: boolean }[] {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startWeekday = firstDay.getDay(); // 0=周日
  const daysInMonth = lastDay.getDate();

  const grid: { date: string; day: number; isCurrentMonth: boolean }[] = [];

  // 上月填充
  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    const prevMonth = month - 1 < 1 ? 12 : month - 1;
    const prevYear = month - 1 < 1 ? year - 1 : year;
    grid.push({
      date: `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: false,
    });
  }

  // 当月
  for (let day = 1; day <= daysInMonth; day++) {
    grid.push({
      date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: true,
    });
  }

  // 下月填充到 42 格（6 周）
  const remaining = 42 - grid.length;
  for (let day = 1; day <= remaining; day++) {
    const nextMonth = month + 1 > 12 ? 1 : month + 1;
    const nextYear = month + 1 > 12 ? year + 1 : year;
    grid.push({
      date: `${nextYear}-${String(nextMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      day,
      isCurrentMonth: false,
    });
  }

  return grid;
}

// ============================================
// 主组件
// ============================================

export default function MarketingCalendar() {
  const dashboard = useDashboardData();
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);
  const [viewMode, setViewMode] = useState<"month" | "week">("month");
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const todayStr = today.toISOString().slice(0, 10);

  // ============================================
  // 加载已有事件（从 Vault 读取）
  // ============================================
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        `/vault-api/read?path=${encodeURIComponent(
          `10_Operations（运营层）/Marketing/Calendar/${viewYear}-${String(viewMonth).padStart(2, "0")}.md`
        )}`
      );
      if (resp.ok) {
        const json = await resp.json();
        if (json?.success && json.content) {
          // 解析 markdown 中的事件表格
          const parsed = parseCalendarFromMarkdown(json.content);
          setEvents(parsed);
          return;
        }
      }
      setEvents([]);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [viewYear, viewMonth]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  // ============================================
  // AI 生成月度营销计划
  // ============================================
  const generateMonthlyPlan = useCallback(async () => {
    setGenerating(true);
    setMessage(null);
    try {
      const nodes = getNodesForMonth(viewYear, viewMonth);
      const newEvents: CalendarEvent[] = [];

      // 基于营销节点生成事件
      nodes.forEach((node, idx) => {
        const eventDate = `${viewYear}-${node.date}`;
        node.platforms.forEach((platform, pIdx) => {
          newEvents.push({
            id: `auto-${idx}-${pIdx}-${eventDate}`,
            date: eventDate,
            platform,
            title: `${node.name}·${PLATFORM_META[platform].label}内容`,
            status: "planned",
            nodeType: node.type,
            note: node.contentAngle,
          });
        });
      });

      // 补充常规内容（每周每平台至少 2 篇）
      const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
      const platforms: MarketingPlatform[] = ["wechat_official", "xiaohongshu", "douyin_personal"];
      platforms.forEach((platform) => {
        // 每周一篇
        for (let week = 0; week < 4; week++) {
          const day = Math.min(1 + week * 7 + 2, daysInMonth); // 每周三
          const eventDate = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          // 避免与营销节点重复
          if (!newEvents.some((e) => e.date === eventDate && e.platform === platform)) {
            newEvents.push({
              id: `regular-${platform}-${week}-${eventDate}`,
              date: eventDate,
              platform,
              title: `常规内容·第${week + 1}周`,
              status: "planned",
              nodeType: "custom",
              note: "基于上周内容数据推荐选题",
            });
          }
        }
      });

      setEvents(newEvents);

      // 持久化到 Vault
      const content = buildCalendarMarkdown(viewYear, viewMonth, newEvents);
      await fetch(
        `/vault-api/write?path=${encodeURIComponent(
          `10_Operations（运营层）/Marketing/Calendar/${viewYear}-${String(viewMonth).padStart(2, "0")}.md`
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );

      setMessage({
        type: "ok",
        text: `✅ 已生成 ${viewYear}年${viewMonth}月营销计划（${newEvents.length} 条事件）\n已写入 Vault：10_Operations（运营层）/Marketing/Calendar/${viewYear}-${String(viewMonth).padStart(2, "0")}.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 生成失败：${(err as Error).message}`,
      });
    } finally {
      setGenerating(false);
    }
  }, [viewYear, viewMonth]);

  // ============================================
  // 日历网格
  // ============================================
  const calendarGrid = useMemo(
    () => generateCalendarGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  // 按日期分组事件
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach((e) => {
      if (!map[e.date]) map[e.date] = [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  // 统计
  const stats = useMemo(() => {
    const byStatus: Record<EventStatus, number> = {
      planned: 0,
      generating: 0,
      ready: 0,
      published: 0,
      failed: 0,
    };
    events.forEach((e) => byStatus[e.status]++);
    return { total: events.length, byStatus };
  }, [events]);

  // 月份切换
  const prevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const nextMonth = () => {
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // 当前选中日期的事件
  const selectedEvents = selectedDate ? eventsByDate[selectedDate] || [] : [];

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <Calendar size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能营销日历</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Marketing Calendar · {stats.total} 条事件 · {stats.byStatus.published} 已发布
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 视图切换 */}
          <div className="flex items-center rounded-md border border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setViewMode("month")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                viewMode === "month"
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              月视图
            </button>
            <button
              onClick={() => setViewMode("week")}
              className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
                viewMode === "week"
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-white/[0.02] text-gray-500 hover:bg-white/[0.04]"
              }`}
            >
              周视图
            </button>
          </div>

          {/* AI 生成 */}
          <button
            onClick={generateMonthlyPlan}
            disabled={generating}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
            title="AI 基于营销节点库生成月度营销计划"
          >
            {generating ? (
              <Loader2 size={11} className="text-emerald-400 animate-spin" />
            ) : (
              <Sparkles size={11} className="text-emerald-400" />
            )}
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              {generating ? "生成中..." : "AI 生成计划"}
            </span>
          </button>

          <button
            onClick={loadEvents}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-gray-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 月份导航 */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.04]">
        <button
          onClick={prevMonth}
          className="w-6 h-6 rounded-md flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
        >
          <ChevronLeft size={12} className="text-gray-400" />
        </button>
        <span className="text-[12px] font-mono text-white tracking-wide">
          {viewYear} 年 {viewMonth} 月
        </span>
        <button
          onClick={nextMonth}
          className="w-6 h-6 rounded-md flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
        >
          <ChevronRight size={12} className="text-gray-400" />
        </button>
      </div>

      {/* 日历主体 */}
      <div className="p-4">
        {/* 星期标题 */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["日", "一", "二", "三", "四", "五", "六"].map((d) => (
            <div
              key={d}
              className="text-center text-[9px] font-mono text-gray-600 uppercase tracking-wider py-1"
            >
              {d}
            </div>
          ))}
        </div>

        {/* 日历网格 */}
        <div className="grid grid-cols-7 gap-1">
          {calendarGrid.map((cell, idx) => {
            const cellEvents = eventsByDate[cell.date] || [];
            const isToday = cell.date === todayStr;
            const isSelected = cell.date === selectedDate;
            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(isSelected ? null : cell.date)}
                className={`relative min-h-[56px] rounded-md border p-1 transition-all text-left ${
                  cell.isCurrentMonth
                    ? "border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.04]"
                    : "border-transparent bg-transparent opacity-30"
                } ${
                  isSelected
                    ? "ring-1 ring-emerald-500/40 border-emerald-500/30"
                    : ""
                }`}
                style={isToday ? { borderColor: "rgba(16,185,129,0.3)" } : undefined}
              >
                <span
                  className={`text-[10px] font-mono ${
                    isToday
                      ? "text-emerald-400 font-bold"
                      : cell.isCurrentMonth
                      ? "text-gray-400"
                      : "text-gray-700"
                  }`}
                >
                  {cell.day}
                </span>
                {/* 事件标记 */}
                <div className="mt-0.5 space-y-0.5">
                  {cellEvents.slice(0, 3).map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-mono truncate"
                      style={{
                        background: `${PLATFORM_META[e.platform].color}20`,
                        color: PLATFORM_META[e.platform].color === "#000000" ? "#9CA3AF" : PLATFORM_META[e.platform].color,
                      }}
                      title={e.title}
                    >
                      <span
                        className="w-1 h-1 rounded-full flex-shrink-0"
                        style={{ background: STATUS_META[e.status].color }}
                      />
                      <span className="truncate">{e.title}</span>
                    </div>
                  ))}
                  {cellEvents.length > 3 && (
                    <div className="text-[8px] font-mono text-gray-600 px-1">
                      +{cellEvents.length - 3} 更多
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* 选中日期的事件详情 */}
        <AnimatePresence>
          {selectedDate && selectedEvents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3 overflow-hidden"
            >
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                    {selectedDate} 的事件（{selectedEvents.length}）
                  </span>
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="text-[9px] font-mono text-gray-600 hover:text-gray-400"
                  >
                    收起
                  </button>
                </div>
                <div className="space-y-1.5">
                  {selectedEvents.map((e) => {
                    const pMeta = PLATFORM_META[e.platform];
                    const sMeta = STATUS_META[e.status];
                    const SIcon = sMeta.icon;
                    return (
                      <div
                        key={e.id}
                        className="flex items-center gap-2 p-2 rounded-md border bg-white/[0.02]"
                        style={{ borderColor: `${pMeta.color}25` }}
                      >
                        <div
                          className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[8px] font-mono font-bold"
                          style={{ background: `${pMeta.color}20`, color: pMeta.color === "#000000" ? "#9CA3AF" : pMeta.color }}
                        >
                          {pMeta.label.slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-gray-200 truncate">{e.title}</div>
                          {e.note && (
                            <div className="text-[9px] font-mono text-gray-600 mt-0.5">{e.note}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                            style={{ background: `${sMeta.color}15`, color: sMeta.color }}
                          >
                            <SIcon size={8} className={e.status === "generating" ? "animate-spin" : ""} />
                            {sMeta.label}
                          </span>
                          {e.status === "planned" && (
                            <button
                              onClick={() => setWorkspace("media_studio")}
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
                              title="前往媒体工作室生成内容"
                            >
                              <ExternalLink size={8} />
                              生成
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 状态图例 */}
        <div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-white/[0.04]">
          {(Object.keys(STATUS_META) as EventStatus[]).map((s) => {
            const m = STATUS_META[s];
            return (
              <div key={s} className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                <span className="text-[9px] font-mono text-gray-500 uppercase">
                  {m.label}({stats.byStatus[s]})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// Markdown 解析/生成工具
// ============================================

function parseCalendarFromMarkdown(content: string): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  const lines = content.split("\n");
  let currentDate = "";
  for (const line of lines) {
    // 匹配日期标题 ### 2026-08-15
    const dateMatch = line.match(/^###\s+(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      currentDate = dateMatch[1];
      continue;
    }
    // 匹配事件行 - [公众号] 标题 [待生成]
    const eventMatch = line.match(/^-\s*\[(.+?)\]\s+(.+?)\s*\[(待生成|生成中|待发布|已发布|失败)\]/);
    if (eventMatch && currentDate) {
      const platformLabel = eventMatch[1];
      const title = eventMatch[2];
      const statusLabel = eventMatch[3];
      const platform = (Object.keys(PLATFORM_META) as MarketingPlatform[]).find(
        (p) => PLATFORM_META[p].label === platformLabel
      ) || "wechat_official";
      const statusMap: Record<string, EventStatus> = {
        待生成: "planned",
        生成中: "generating",
        待发布: "ready",
        已发布: "published",
        失败: "failed",
      };
      events.push({
        id: `parsed-${events.length}-${currentDate}`,
        date: currentDate,
        platform,
        title,
        status: statusMap[statusLabel] || "planned",
      });
    }
  }
  return events;
}

function buildCalendarMarkdown(year: number, month: number, events: CalendarEvent[]): string {
  const date = new Date().toISOString();
  const lines: string[] = [
    "---",
    "type: marketing_calendar",
    `year: ${year}`,
    `month: ${month}`,
    `created_at: ${date}`,
    `total_events: ${events.length}`,
    "---",
    "",
    `# 📅 营销日历 · ${year}年${month}月`,
    "",
    `> 自动生成于 ${date}，共 ${events.length} 条营销事件`,
    "",
  ];

  // 按日期分组
  const byDate: Record<string, CalendarEvent[]> = {};
  events.forEach((e) => {
    if (!byDate[e.date]) byDate[e.date] = [];
    byDate[e.date].push(e);
  });

  const sortedDates = Object.keys(byDate).sort();
  for (const date of sortedDates) {
    lines.push(`### ${date}`);
    lines.push("");
    for (const e of byDate[date]) {
      const pMeta = PLATFORM_META[e.platform];
      const sMeta = STATUS_META[e.status];
      lines.push(`- [${pMeta.label}] ${e.title} [${sMeta.label}]${e.note ? ` — ${e.note}` : ""}`);
    }
    lines.push("");
  }

  lines.push("---", `生成时间：${date}`);
  return lines.join("\n");
}
